/**
 * Panel de desarrollo.
 *
 * Solo visible cuando `import.meta.env.DEV === true`. Dos pestañas:
 *
 *   1. Seed & reset   → botón para limpiar toda la base de datos y botón
 *      para cargar datos fake (5 ventas, 3 devoluciones, 3 clientes, 4
 *      partners, 3 OC, 5 filas de inventario). El backend también valida
 *      el modo antes de ejecutar cualquier acción destructiva, así que en
 *      producción estos endpoints devuelven 403 aunque alguien los llame
 *      manualmente.
 *
 *   2. Activity log  → vista unificada de todo lo que hace la app:
 *      - Requests HTTP (request/response/error) del frontend y del backend
 *      - Errores globales (window.error, unhandledrejection)
 *      - Mensajes de console.error / console.warn
 *      - Eventos de boot y cualquier `log()` manual
 *      Incluye filtros por nivel/fuente, búsqueda full-text, auto-refresh
 *      y botón para vaciar el buffer tanto local como en el servidor.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Activity,
  Database,
  Eraser,
  FileText,
  Pause,
  Play,
  RefreshCw,
  Terminal,
  Trash2,
} from 'lucide-react'
import { toast } from 'sonner'
import { ModuleShell } from '@/components/ui/ModuleShell'
import { Button } from '@/components/ui/Button'
import { apiCall } from '@/lib/api'
import {
  clear as clearFrontendLogs,
  getAll as getFrontendLogs,
  subscribe as subscribeFrontendLogs,
} from '@/lib/devLog'

const TABS = [
  { id: 'seed', label: 'Seed & reset', icon: Database },
  { id: 'log', label: 'Activity log', icon: Activity },
]

export default function DevPage() {
  const [tab, setTab] = useState('seed')

  return (
    <ModuleShell
      title="Modo desarrollo"
      subtitle="Herramientas de limpieza, semillado y observabilidad"
      icon={Terminal}
    >
      <div className="mb-4 flex gap-2 border-b border-expo-line">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={[
              'flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab === id
                ? 'border-expo-primary text-expo-primary'
                : 'border-transparent text-gray-500 hover:text-expo-dark',
            ].join(' ')}
          >
            <Icon size={16} />
            {label}
          </button>
        ))}
      </div>

      {tab === 'seed' ? <SeedPanel /> : <LogPanel />}
    </ModuleShell>
  )
}

/* ─── Seed & reset ─────────────────────────────────────────────────── */

function SeedPanel() {
  const [working, setWorking] = useState(null)
  const [serverEnabled, setServerEnabled] = useState(null)
  const [lastAction, setLastAction] = useState(null)
  const [testResult, setTestResult] = useState(null)

  useEffect(() => {
    apiCall('/dev/enabled')
      .then((r) => setServerEnabled(Boolean(r?.enabled)))
      .catch(() => setServerEnabled(false))
  }, [])

  const runReset = async () => {
    if (!confirm('Esto borra TODOS los datos. ¿Continuar?')) return
    setWorking('reset')
    try {
      const r = await apiCall('/dev/reset', { method: 'POST' })
      setLastAction({ type: 'reset', payload: r, at: new Date().toISOString() })
      toast.success('Base de datos limpiada')
    } catch (err) {
      toast.error(err.message)
    } finally {
      setWorking(null)
    }
  }

  const runSeed = async () => {
    if (!confirm('Esto reemplaza los datos actuales con datos fake. ¿Continuar?')) return
    setWorking('seed')
    try {
      const r = await apiCall('/dev/seed', { method: 'POST' })
      setLastAction({ type: 'seed', payload: r, at: new Date().toISOString() })
      toast.success('Datos fake cargados')
    } catch (err) {
      toast.error(err.message)
    } finally {
      setWorking(null)
    }
  }

  const runTests = async () => {
    if (!confirm('Correr la suite de pruebas. ⚠ Esto RESETEA y resiembra los datos. ¿Continuar?')) return
    setWorking('tests')
    setTestResult(null)
    try {
      const r = await apiCall('/dev/run-tests', { method: 'POST' })
      setTestResult(r)
      if (r.ok) toast.success(`Pruebas OK: ${r.pass}/${r.total}`)
      else toast.error(`Pruebas con fallos: ${r.fail} de ${r.total}`)
    } catch (err) {
      toast.error(err.message)
    } finally {
      setWorking(null)
    }
  }

  return (
    <div>
      <div className="mb-6 rounded-xl border-2 border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
        <div className="font-semibold mb-1">⚠ Atención</div>
        <p>
          Estas acciones son destructivas y reemplazan el contenido de{' '}
          <code>db/data.json</code>. Están reservadas al entorno de desarrollo.
          Estado del backend:{' '}
          {serverEnabled === null
            ? 'consultando…'
            : serverEnabled
            ? 'dev mode ✅'
            : 'deshabilitado (403)'}
          .
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-expo-line bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <Eraser size={20} className="text-red-500" />
            <h3 className="text-lg font-semibold text-expo-dark">
              Borrar todos los datos
            </h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Deja todas las colecciones vacías (ventas, devoluciones, clientes,
            partners, compras, inventario). Útil para empezar una demo desde
            cero.
          </p>
          <Button
            type="button"
            variant="danger"
            disabled={working !== null || serverEnabled === false}
            onClick={runReset}
          >
            {working === 'reset' ? (
              <>
                <RefreshCw size={16} className="animate-spin" />
                <span>Limpiando…</span>
              </>
            ) : (
              <>
                <Eraser size={16} />
                <span>Limpiar base de datos</span>
              </>
            )}
          </Button>
        </div>

        <div className="rounded-2xl border border-expo-line bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <Database size={20} className="text-expo-primary" />
            <h3 className="text-lg font-semibold text-expo-dark">
              Cargar datos fake
            </h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Reemplaza lo actual con un set de prueba: 4 partners, 3 clientes,
            5 ventas en distintos estados, 3 órdenes de compra, 5 filas de
            inventario y 3 devoluciones.
          </p>
          <Button
            type="button"
            disabled={working !== null || serverEnabled === false}
            onClick={runSeed}
          >
            {working === 'seed' ? (
              <>
                <RefreshCw size={16} className="animate-spin" />
                <span>Generando…</span>
              </>
            ) : (
              <>
                <Database size={16} />
                <span>Generar datos fake</span>
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="mt-4 rounded-2xl border border-expo-line bg-white p-5 shadow-sm">
        <div className="flex items-center gap-3 mb-2">
          <Play size={20} className="text-expo-primary" />
          <h3 className="text-lg font-semibold text-expo-dark">Correr pruebas</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Ejecuta la suite de tests de backend (invariantes de datos: variedades,
          combos, devoluciones coherentes, etc.) contra esta instancia.{' '}
          <strong>Resetea y resiembra los datos.</strong>
        </p>
        <Button
          type="button"
          disabled={working !== null || serverEnabled === false}
          onClick={runTests}
        >
          {working === 'tests' ? (
            <>
              <RefreshCw size={16} className="animate-spin" />
              <span>Corriendo…</span>
            </>
          ) : (
            <>
              <Play size={16} />
              <span>Correr pruebas</span>
            </>
          )}
        </Button>
        {testResult && (
          <div className="mt-4">
            <div
              className={`text-sm font-semibold mb-2 ${
                testResult.ok ? 'text-emerald-600' : 'text-red-600'
              }`}
            >
              {testResult.ok ? '✓' : '✗'} {testResult.pass}/{testResult.total} pasaron
              {testResult.fail ? ` · ${testResult.fail} fallaron` : ''}
            </div>
            <pre className="max-h-80 overflow-auto rounded-lg bg-gray-900 text-gray-100 text-xs p-3 whitespace-pre-wrap">
              {testResult.output}
            </pre>
          </div>
        )}
      </div>

      {lastAction && (
        <div className="mt-6 rounded-xl border border-expo-line bg-gray-50 p-4">
          <div className="text-xs text-gray-500 mb-1">
            Última acción · {new Date(lastAction.at).toLocaleString('es-CO')}
          </div>
          <pre className="text-xs text-expo-dark whitespace-pre-wrap">
            {JSON.stringify(lastAction.payload, null, 2)}
          </pre>
        </div>
      )}
    </div>
  )
}

/* ─── Activity log ─────────────────────────────────────────────────── */

function LogPanel() {
  const [backendLogs, setBackendLogs] = useState([])
  const [frontendLogs, setFrontendLogs] = useState(() => getFrontendLogs())
  const [paused, setPaused] = useState(false)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [filter, setFilter] = useState({
    level: 'all',         // all | info | warn | error
    source: 'all',        // all | frontend | backend
    search: '',
  })
  const [selected, setSelected] = useState(null)
  const backendSinceIdRef = useRef(0)
  const containerRef = useRef(null)

  // Suscripción en vivo al store del frontend.
  useEffect(() => {
    const unsub = subscribeFrontendLogs((entries) => {
      if (!paused) setFrontendLogs([...entries])
    })
    return unsub
  }, [paused])

  // Polling al backend para incrementos nuevos.
  const fetchBackendLogs = useCallback(async () => {
    try {
      const list = await apiCall(
        `/dev/logs?sinceId=${backendSinceIdRef.current}`,
      )
      if (Array.isArray(list) && list.length > 0) {
        backendSinceIdRef.current = Math.max(
          backendSinceIdRef.current,
          ...list.map((r) => Number(r.id) || 0),
        )
        setBackendLogs((prev) => {
          const merged = [...prev, ...list]
          // Mantener el tope en 500 para no desbordar memoria del panel.
          return merged.length > 500 ? merged.slice(merged.length - 500) : merged
        })
      }
    } catch {
      /* silencioso — si el backend está caído igual queremos mostrar el front */
    }
  }, [])

  useEffect(() => {
    fetchBackendLogs()
  }, [fetchBackendLogs])

  useEffect(() => {
    if (!autoRefresh || paused) return undefined
    const id = setInterval(fetchBackendLogs, 2000)
    return () => clearInterval(id)
  }, [autoRefresh, paused, fetchBackendLogs])

  // Lista combinada, ordenada cronológicamente por `at`.
  const combined = useMemo(() => {
    const list = [...backendLogs, ...frontendLogs]
    list.sort((a, b) => String(a.at).localeCompare(String(b.at)))
    return list
  }, [backendLogs, frontendLogs])

  const filtered = useMemo(() => {
    const q = filter.search.trim().toLowerCase()
    return combined.filter((r) => {
      if (filter.level !== 'all' && r.level !== filter.level) return false
      if (filter.source !== 'all' && r.source !== filter.source) return false
      if (!q) return true
      const haystack = [
        r.message,
        r.path,
        r.method,
        r.component,
        r.phase,
        String(r.status ?? ''),
        r.detail ? JSON.stringify(r.detail) : '',
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return haystack.includes(q)
    })
  }, [combined, filter])

  // Auto-scroll al final cuando llega algo nuevo (si no está pausado).
  useEffect(() => {
    if (paused) return
    const el = containerRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [filtered.length, paused])

  const clearAll = async () => {
    if (!confirm('Vaciar el activity log local y del servidor?')) return
    clearFrontendLogs()
    setBackendLogs([])
    backendSinceIdRef.current = 0
    try {
      await apiCall('/dev/logs', { method: 'DELETE' })
      toast.success('Logs vaciados')
    } catch (err) {
      toast.error(`No se pudo vaciar backend: ${err.message}`)
    }
  }

  const counts = useMemo(() => {
    const c = { info: 0, warn: 0, error: 0, total: combined.length }
    for (const r of combined) c[r.level] = (c[r.level] || 0) + 1
    return c
  }, [combined])

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-expo-line bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <FileText size={16} />
            <span>
              {counts.total} entradas · {counts.info} info ·{' '}
              <span className="text-amber-600">{counts.warn} warn</span> ·{' '}
              <span className="text-red-600">{counts.error} error</span>
            </span>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setPaused((p) => !p)}
              title={paused ? 'Reanudar' : 'Pausar'}
            >
              {paused ? <Play size={14} /> : <Pause size={14} />}
              <span>{paused ? 'Reanudar' : 'Pausar'}</span>
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={fetchBackendLogs}
              title="Refrescar ahora"
            >
              <RefreshCw size={14} />
              <span>Refrescar</span>
            </Button>
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              Auto-refresh (2s)
            </label>
            <Button type="button" variant="danger" onClick={clearAll}>
              <Trash2 size={14} />
              <span>Vaciar</span>
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <input
            type="text"
            value={filter.search}
            onChange={(e) =>
              setFilter((f) => ({ ...f, search: e.target.value }))
            }
            placeholder="Buscar… (ruta, método, mensaje, payload)"
            className="flex-1 min-w-[240px] rounded-lg border border-expo-line px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-expo-primary"
          />
          <select
            value={filter.level}
            onChange={(e) =>
              setFilter((f) => ({ ...f, level: e.target.value }))
            }
            className="rounded-lg border border-expo-line px-3 py-2 text-sm"
          >
            <option value="all">Todos los niveles</option>
            <option value="info">Info</option>
            <option value="warn">Warn</option>
            <option value="error">Error</option>
          </select>
          <select
            value={filter.source}
            onChange={(e) =>
              setFilter((f) => ({ ...f, source: e.target.value }))
            }
            className="rounded-lg border border-expo-line px-3 py-2 text-sm"
          >
            <option value="all">Todas las fuentes</option>
            <option value="frontend">Frontend</option>
            <option value="backend">Backend</option>
          </select>
        </div>
      </div>

      <div
        ref={containerRef}
        className="h-[520px] overflow-auto rounded-xl border border-expo-line bg-gray-900 font-mono text-xs shadow-sm"
      >
        {filtered.length === 0 ? (
          <div className="p-6 text-center text-gray-400">
            Sin entradas que coincidan con el filtro.
          </div>
        ) : (
          <ul className="divide-y divide-gray-800">
            {filtered.map((r, i) => (
              <li
                key={`${r.source}-${r.id}-${r.at}-${i}`}
                className="cursor-pointer hover:bg-gray-800 px-3 py-1.5"
                onClick={() => setSelected(r)}
              >
                <LogRow row={r} />
              </li>
            ))}
          </ul>
        )}
      </div>

      {selected && (
        <LogDetailModal entry={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  )
}

function LogRow({ row }) {
  const time = new Date(row.at).toLocaleTimeString('es-CO', { hour12: false })
  const levelColor = {
    info: 'text-emerald-300',
    warn: 'text-amber-300',
    error: 'text-red-400',
  }[row.level] || 'text-gray-300'
  const sourceBadge =
    row.source === 'backend'
      ? 'bg-indigo-900 text-indigo-200'
      : 'bg-teal-900 text-teal-200'
  return (
    <div className="flex items-start gap-2">
      <span className="shrink-0 text-gray-500">{time}</span>
      <span className={`shrink-0 uppercase font-semibold w-12 ${levelColor}`}>
        {row.level}
      </span>
      <span className={`shrink-0 rounded px-1.5 ${sourceBadge}`}>
        {row.source}
      </span>
      {row.method && (
        <span className="shrink-0 text-blue-300">{row.method}</span>
      )}
      {row.status != null && (
        <span
          className={`shrink-0 ${
            row.status >= 500
              ? 'text-red-400'
              : row.status >= 400
              ? 'text-amber-300'
              : 'text-emerald-300'
          }`}
        >
          {row.status}
        </span>
      )}
      {row.durationMs != null && (
        <span className="shrink-0 text-gray-500">{row.durationMs}ms</span>
      )}
      <span className="flex-1 text-gray-100 truncate">{row.message}</span>
    </div>
  )
}

function LogDetailModal({ entry, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-expo-line px-5 py-3">
          <div>
            <div className="text-sm font-semibold text-expo-dark">
              {entry.message}
            </div>
            <div className="text-xs text-gray-500">
              {new Date(entry.at).toLocaleString('es-CO')} · {entry.source} ·{' '}
              {entry.level} · {entry.phase}
            </div>
          </div>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-700"
            onClick={onClose}
          >
            ✕
          </button>
        </div>
        <div className="max-h-[70vh] overflow-auto p-5">
          <pre className="whitespace-pre-wrap text-xs text-expo-dark">
            {JSON.stringify(entry, null, 2)}
          </pre>
        </div>
      </div>
    </div>
  )
}
